import {useIntl} from 'react-intl';
import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {Card} from '@material-ui/core';
import {fetchDeploymentStatusStateless} from '../../store/prodDeployments/dashboard';
import {DeploymentStatusInfo} from '../../types/deployment/DeploymentStatusInfo';
import {RootState} from '../../store';
import {VideoStreamController} from '../TestDeployView/HlsVideoStream';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../TestDeployView/HlsVideoStream';
import {DefaultMonitorConfig} from '../TestDeployView/HlsVideoStream';
import {VideoStream} from '../TestDeployView/HlsVideoStream';

type EdgeStreamContainerProps = {
  projectId: string;
  deploymentId: string;
  streamSubject: VideoStreamSubject;
  onFinish?: () => void;
  onRunning?: () => void;
  onError?: (message?: string) => void;
};

const UPDATE_INTERVAL = 2000;

export const EdgeStreamContainer = ({
  projectId,
  deploymentId,
  streamSubject,
  onError,
  onFinish,
  onRunning,
}: EdgeStreamContainerProps) => {
  const intl = useIntl();
  const [streamReady, setStreamReady] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [videoController, setVideoController] = useState<VideoStreamController>();
  const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatusInfo>();
  const serverConfig = useSelector((state: RootState) => state.config);

  const onErrorRef = useRef(onError);
  const onFinishRef = useRef(onFinish);
  const onRunningRef = useRef(onRunning);

  useEffect(() => {
    function isPollingNeeded(deployment?: DeploymentStatusInfo) {
      return (
        !deployment || (deployment.status !== 'RUNNING' && deployment.status !== 'ERROR')
      );
    }
    function isError(deployment?: DeploymentStatusInfo) {
      return (
        deployment &&
        (deployment.status === 'ERROR' ||
          (deployment.status === 'IDLE' && deployment.errorMessage))
      );
    }

    async function fetchDeploymentStatus() {
      try {
        if (isPollingNeeded(deploymentStatus)) {
          const existingStatus = await fetchDeploymentStatusStateless(deploymentId);
          if (isError(existingStatus)) {
            streamSubject.raiseInitErrorEvent(existingStatus.errorMessage);
          } else if (isPollingNeeded(existingStatus)) {
            setTimeout(() => setDeploymentStatus(existingStatus), UPDATE_INTERVAL);
          } else {
            setDeploymentStatus(existingStatus);
          }
        }
      } catch (e) {
        streamSubject.raiseInitErrorEvent(e);
      }
    }

    fetchDeploymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentStatus]);

  class EdgeSpaStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.CONNECTED:
          onRunningRef.current?.();
          setStreamConnected(true);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
          onErrorRef.current?.(
            intl.formatMessage({id: 'edgeStream.error.connectionFailed'})
          );
          setStreamConnected(false);
          setStreamReady(false);
          break;
        case EVENT_TYPE.REMOTE_STREAM:
          onRunningRef.current?.();
          setStreamReady(true);
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            event.message = intl.formatMessage(
              {id: 'edgeStream.error.customError'},
              {errorMessage: event.message}
            );
          } else {
            event.message = intl.formatMessage({
              id: 'edgeStream.error.defaultError',
            });
          }

          onErrorRef.current?.(event.message);
          event.deploymentId = deploymentId;
          setStreamReady(false);
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          onFinishRef.current?.();
          event.deploymentId = deploymentId;
          setStreamReady(false);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    const controller: VideoStreamController = new VideoStreamController();
    streamSubject.addListener('EdgeSpaStreamObserver', new EdgeSpaStreamObserver());

    if (
      deploymentStatus?.videoOutputHlsUrl &&
      projectId &&
      deploymentStatus.status === 'RUNNING'
    ) {
      controller.start({
        limitedDuration: false,
        source: 'IP_CAMERA',
        url: deploymentStatus.videoOutputHlsUrl,
        streamModel: 'PUSH',
        projectId,
        serverConfig,
        monitorConfig: {
          ...DefaultMonitorConfig,
          monitorStream: true,
          projectId,
          deploymentId: deploymentId,
        },
        streamSubject,
      });

      const cleanup = () => {
        streamSubject.raiseStreamFinishEvent();
      };
      setVideoController(controller);
      window.addEventListener('beforeunload', cleanup);

      return () => {
        window.removeEventListener('beforeunload', cleanup);
        cleanup();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentStatus, serverConfig]);

  return (
    <div className="prod-deploy-details">
      <div className="prod-deploy-details__body">
        <Card className="prod-deploy-details__results">
          <VideoStream
            className="prod-deploy-details__video"
            hlsRef={videoController?.hlsClient.hlsRef}
            streamConnected={streamConnected}
            streamReady={streamReady}
            monitor={videoController?.monitor}
          />
        </Card>
      </div>
    </div>
  );
};
